import { Button, Grid } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import { useDeleteDraftMutation, useDownloadDraftMutation } from "../../services/draftsService";
import TableService from "../../services/tableService";
import { DocxDraftAddDialog } from "./DocxDrafts/DocxDraftAddDialog";
import { DocxDraftEditDialog } from "./DocxDrafts/DocxDraftEditDialog";
import { DocxDraftType } from "./DocxDrafts/DocxDraftType";
import {
    AGREEMENT_DRAFTS_TABLE_NAME,
    useDocxDraftsTableFieldsConfig,
} from "./DocxDrafts/DocxDraftsTableFieldsConfig";

export function AgreementsTab({
    draftsDataLocal,
    setDraftsDataLocal,
    recordCount,
    resetPageNumber,
    selectedRowId,
    setSelectedRowId,
    hiddenColumnsForTables,
    columnsOrdersForTables,
    checkedAll,
    setCheckedAll,
    dataPage,
    handleChangeDataPage,
    dataPageSize,
    handleChangeDataPageSize,
    searchParams,
}) {
    const { t } = useTranslation();
    const TABLE_FIELDS_CONFIG = useDocxDraftsTableFieldsConfig();

    const [addDraftOpen, setAddDraftOpen] = useState(false);
    const [editDraftDialogOpen, setEditDraftDialogOpen] = useState(false);
    const [selectedDraftForEdit, setSelectedDraftForEdit] = useState(null);

    const downloadDraftMutation = useDownloadDraftMutation();
    const deleteDraftMutation = useDeleteDraftMutation({
        onSuccess: () => {
            toast.success(t("drafts.deleteSuccess"));
        },
        onError: () => {
            toast.error(t("drafts.deleteError"));
        },
    });

    const onClickDraftButton = useCallback(
        (e, id, action) => {
            e.stopPropagation();

            if (action === "edit") {
                const draft = draftsDataLocal.find((o) => o.id === id);
                if (draft) {
                    setSelectedDraftForEdit(draft);
                    setEditDraftDialogOpen(true);
                }
            }
            if (action === "download") {
                const draft = draftsDataLocal.find((o) => o.id === id);
                if (draft) {
                    downloadDraftMutation.mutate(
                        {
                            draftId: draft.id,
                        },
                        {
                            onSuccess: (data) => {
                                const url = window.URL.createObjectURL(data);
                                const link = document.createElement("a");
                                link.href = url;
                                link.setAttribute("download", draft.file_name);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            },
                        },
                    );
                }
            }
            if (action === "delete") {
                deleteDraftMutation.mutate({ draftId: id });
            }
        },
        [draftsDataLocal, downloadDraftMutation, deleteDraftMutation],
    );

    const draftsDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            TABLE_FIELDS_CONFIG,
            Array.isArray(hiddenColumnsForTables?.[AGREEMENT_DRAFTS_TABLE_NAME])
                ? hiddenColumnsForTables[AGREEMENT_DRAFTS_TABLE_NAME]
                : [],
            Array.isArray(columnsOrdersForTables?.[AGREEMENT_DRAFTS_TABLE_NAME])
                ? columnsOrdersForTables[AGREEMENT_DRAFTS_TABLE_NAME]
                : [],
        );
    }, [TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const draftsData = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                TABLE_FIELDS_CONFIG,
                draftsDataLocal,
                Array.isArray(hiddenColumnsForTables?.[AGREEMENT_DRAFTS_TABLE_NAME])
                    ? hiddenColumnsForTables[AGREEMENT_DRAFTS_TABLE_NAME]
                    : [],
                Array.isArray(columnsOrdersForTables?.[AGREEMENT_DRAFTS_TABLE_NAME])
                    ? columnsOrdersForTables[AGREEMENT_DRAFTS_TABLE_NAME]
                    : [],
                onClickDraftButton,
            ),
        [
            TABLE_FIELDS_CONFIG,
            draftsDataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickDraftButton,
        ],
    );

    return (
        <>
            <Grid container spacing={10} justifyContent="left" sx={{ mb: 2 }}>
                <Grid item>
                    <Button variant="contained" onClick={() => setAddDraftOpen(true)}>
                        {t("drafts.agreementDrafts.addDraft")}
                    </Button>
                </Grid>
            </Grid>
            <FilterTable
                //@ts-ignore
                data={draftsData}
                countRecords={recordCount}
                resetPageNumber={resetPageNumber}
                selectedItemId={selectedRowId}
                style={{ maxHeight: "60vh" }}
                headersConfig={TableService.orderColumns(
                    TableService.hideHiddenColumns(
                        TABLE_FIELDS_CONFIG,
                        Array.isArray(hiddenColumnsForTables?.[AGREEMENT_DRAFTS_TABLE_NAME])
                            ? hiddenColumnsForTables[AGREEMENT_DRAFTS_TABLE_NAME]
                            : [],
                    ),
                    Array.isArray(columnsOrdersForTables?.[AGREEMENT_DRAFTS_TABLE_NAME])
                        ? columnsOrdersForTables[AGREEMENT_DRAFTS_TABLE_NAME]
                        : [],
                )}
                showCleanFilterIcon={false}
                showContextMenu={false}
                showExportToFileButton={false}
                exportToFileUrl={"offers/export/"}
                exportToFileSearchParams={searchParams}
                exportToFileHeaders={draftsDataExportHeaders}
                exportToFileFileName={`${t("drafts.agreementDrafts.exportFileName")}.xlsx`}
                showDetailsIcon={false}
                showCheckbox={false}
                checkedAll={checkedAll}
                page={dataPage}
                onPageChange={handleChangeDataPage}
                rowsPerPage={dataPageSize}
                onRowsPerPageChange={handleChangeDataPageSize}
                setDataLocal={setDraftsDataLocal}
                setCheckedAll={setCheckedAll}
                setSelectedRowId={setSelectedRowId}
                showCounterRecords={false}
                hideFiltersIconCol={true}
            />
            <DocxDraftAddDialog
                title={t("drafts.agreementDrafts.addDraft")}
                open={addDraftOpen}
                onClose={() => setAddDraftOpen(false)}
                mode={DocxDraftType.AGREEMENT}
            />
            <DocxDraftEditDialog
                title={t("drafts.agreementDrafts.editDraft")}
                open={editDraftDialogOpen}
                onClose={() => {
                    setEditDraftDialogOpen(false);
                    setSelectedDraftForEdit(null);
                }}
                draft={selectedDraftForEdit}
                onSuccessUpdate={() => {}}
            />
        </>
    );
}
